import React from "react";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { usePagesTranslation } from "../../../locales";
import { WORD_WHEEL_BONUS_WORD_GIFT } from "../constants";
import TreasureChestIcon from "./TreasureChestIcon";

export default function TreasureBonusWordsDialog({
    open,
    onClose,
    onWordPress,
    words = [],
    giftCoins = WORD_WHEEL_BONUS_WORD_GIFT,
}) {
    const { t } = usePagesTranslation();
    const list = Array.isArray(words) ? words : [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        bgcolor: "rgba(8, 47, 73, 0.96)",
                        color: "#f0f9ff",
                        border: "1.5px solid rgba(250, 204, 21, 0.55)",
                        p: 2.25,
                    },
                },
            }}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(254, 243, 199, 0.16)",
                        border: "2px solid #facc15",
                    }}
                >
                    <TreasureChestIcon sx={{ fontSize: 28, color: "#facc15" }} />
                </Box>
                <IconButton
                    onClick={onClose}
                    aria-label={t("wordwheel.treasure-close")}
                    sx={{ color: "rgba(224, 242, 254, 0.72)", minWidth: 44, minHeight: 44 }}
                >
                    <CloseIcon />
                </IconButton>
            </Stack>
            <Typography sx={{ mt: 1.5, fontWeight: 800, fontSize: "1.25rem" }}>
                {t("wordwheel.treasure-title")}
            </Typography>
            <Typography sx={{ mt: 0.75, opacity: 0.78, lineHeight: 1.45 }}>
                {t("wordwheel.treasure-subtitle", { count: giftCoins })}
            </Typography>
            {list.length === 0 ? (
                <Typography sx={{ mt: 2.5, mb: 1, textAlign: "center", opacity: 0.78, lineHeight: 1.5 }}>
                    {t("wordwheel.treasure-empty")}
                </Typography>
            ) : (
                <Stack spacing={1} sx={{ mt: 1.75, maxHeight: 280, overflowY: "auto" }}>
                    {list.map((raw) => {
                        const word = String(raw || "").trim().toUpperCase();
                        if (!word) return null;
                        return (
                            <Button
                                key={word}
                                onClick={() => onWordPress?.(word)}
                                aria-label={t("wordwheel.treasure-lookup", { word })}
                                sx={{
                                    justifyContent: "space-between",
                                    textTransform: "none",
                                    minHeight: 48,
                                    px: 1.5,
                                    borderRadius: 2,
                                    color: "#f0f9ff",
                                    bgcolor: "rgba(224, 242, 254, 0.1)",
                                    border: "1px solid #eab308",
                                    "&:hover": { bgcolor: "rgba(250, 204, 21, 0.16)" },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                    <MenuBookIcon sx={{ fontSize: 18, color: "#5eead4" }} />
                                    <Typography sx={{ fontWeight: 800, letterSpacing: 1 }}>{word}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "#ca8a04" }}>
                                    <CardGiftcardIcon sx={{ fontSize: 16 }} />
                                    <Typography sx={{ fontWeight: 800, fontSize: "0.8rem" }}>
                                        {t("wordwheel.treasure-gift", { count: giftCoins })}
                                    </Typography>
                                </Stack>
                            </Button>
                        );
                    })}
                </Stack>
            )}
            <Button
                onClick={onClose}
                variant="contained"
                fullWidth
                sx={{
                    mt: 2,
                    minHeight: 46,
                    borderRadius: 2,
                    fontWeight: 800,
                    bgcolor: "#0d9488",
                    "&:hover": { bgcolor: "#0f766e" },
                }}
            >
                {t("wordwheel.treasure-close")}
            </Button>
        </Dialog>
    );
}
